<template>
    <jet-form-section @submitted="updateProfileInformation">
        <template #title>
            {{ trans('profile.information.header') }}
        </template>

        <template #form>
            <p class="mb-5">{{ trans('profile.information.description') }}</p>
            <!-- Profile Photo -->
            <div v-if="$page.props.jetstream.managesProfilePhotos" class="form-group form-row">
                <!-- Profile Photo File Input -->
                <input ref="photo" class="d-none" type="file" @change="updatePhotoPreview" />

                <label class="col-form-label col-sm-3">{{ trans('profile.information.label.photo') }}</label>

                <div class="col-sm-9">
                    <!-- Current Profile Photo -->
                    <div v-show="!photoPreview" class="mt-2">
                        <img :alt="user.name" :src="user.profile_photo_url" class="h-20 w-20 rounded-full object-cover" />
                    </div>

                    <!-- New Profile Photo Preview -->
                    <div v-show="photoPreview" class="mt-2">
                        <span
                            :style="
                                'background-size: cover; background-repeat: no-repeat; background-position: center center; background-image: url(\'' +
                                photoPreview +
                                '\');'
                            "
                            class="block h-20 w-20 rounded-full"
                        >
                        </span>
                    </div>

                    <jet-button class="mt-2 mr-2" color="secondary" type="button" @click.prevent="selectNewPhoto">
                        {{ trans('profile.information.action.select_photo') }}
                    </jet-button>

                    <jet-button v-if="user.profile_photo_path" class="mt-2" color="secondary" type="button" @click.prevent="deletePhoto">
                        {{ trans('profile.information.action.remove_photo') }}
                    </jet-button>

                    <jet-input-error :message="form.errors.photo" class="mt-2" />
                </div>
            </div>

            <!-- Name -->
            <div class="col-span-6 sm:col-span-4">
                <jet-input
                    v-model="form.firstName"
                    :invalidFeedback="form.errors.firstName"
                    :label="trans('users.edit_user.field.first_name.label')"
                    :readonly="!isEditable"
                    autocomplete="given-name"
                />
            </div>

            <div class="col-span-6 sm:col-span-4">
                <jet-input
                    v-model="form.lastName"
                    :invalidFeedback="form.errors.lastName"
                    :label="trans('users.edit_user.field.last_name.label')"
                    :readonly="!isEditable"
                    autocomplete="family-name"
                />
            </div>

            <!-- Email -->
            <div class="col-span-6 sm:col-span-4">
                <jet-input
                    v-model="form.email"
                    :invalidFeedback="form.errors.email"
                    :label="trans('profile.information.label.email')"
                    :readonly="!isEditable"
                    autocomplete="email"
                />
            </div>
        </template>

        <template #actions>
            <jet-action-message :on="form.recentlySuccessful" class="mr-3">
                {{ trans('general.message.saved') }}
            </jet-action-message>

            <jet-button v-if="isEditable" :disabled="!form.isDirty" :is-processing="form.processing" color="primary"
                >{{ trans('general.action.save') }}
            </jet-button>
        </template>
    </jet-form-section>
</template>

<script setup>
import JetButton from '@/Jetstream/Button.vue';
import JetFormSection from '@/Jetstream/FormSection.vue';
import JetInput from '@/Jetstream/Input.vue';
import JetInputError from '@/Jetstream/InputError.vue';
import JetActionMessage from '@/Jetstream/ActionMessage.vue';
import { computed, ref } from 'vue';
import { router, useForm, usePage } from '@inertiajs/vue3';

const page = usePage();

const photo = ref(null);
const photoPreview = ref(null);

const user = computed(() => page.props.user);
const isEditable = computed(() => page.props.isEditable);

const form = useForm({
    _method: 'PUT',
    firstName: user.value?.firstName,
    lastName: user.value?.lastName,
    email: user.value?.email,
    photo: null,
});

const clearPhotoFileInput = () => {
    if (photo.value?.value) {
        photo.value.value = null;
    }
};

const updatePhotoPreview = () => {
    const file = photo.value?.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (e) => {
        photoPreview.value = e.target.result;
    };
    reader.readAsDataURL(file);
};

const selectNewPhoto = () => {
    photo.value?.click();
};

const deletePhoto = () => {
    router.delete(route('current-user-photo.destroy'), {
        preserveScroll: true,
        onSuccess: () => {
            photoPreview.value = null;
            clearPhotoFileInput();
        },
    });
};

const updateProfileInformation = () => {
    if (photo.value?.files?.[0]) {
        form.photo = photo.value.files[0];
    }
    form.post(route('user-profile-information.update'), {
        errorBag: 'updateProfileInformation',
        preserveScroll: true,
        onSuccess: () => clearPhotoFileInput(),
    });
};
</script>
